// Support fix 2026-09-24: public view of a shared ATC post (demand / Sellers Bazaar item).
// Anyone with the link can see it; logging in is only needed to bid / make an offer.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ShareBar from '../../components/atc/ShareBar';

export default function PublicAtcPost() {
  const { kind, id } = useParams();
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const isSale = kind === 'sale';
  const ref = new URLSearchParams(location.search).get('ref');

  useEffect(() => {
    const url = isSale ? `/atc/sale-post/${id}` : `/atc/demands/${id}`;
    api.get(url)
      .then((r) => setPost(isSale ? r.data : r.data?.demand))
      .catch(() => setError(true));
  }, [id, isSale]);

  const isB2B = post?.deal_type === 'B2B_BULK';
  const appPath = `/atc/${isB2B ? 'b2b' : 'b2c'}?${isSale ? 'sale' : 'demand'}=${id}${ref ? `&ref=${encodeURIComponent(ref)}` : ''}`;

  const openInApp = () => {
    if (user) { navigate(appPath); return; }
    try { sessionStorage.setItem('after_login_redirect', appPath); } catch (_) {}
    navigate('/login');
  };

  if (error) {
    return (
      <div className="min-h-screen pt-28 px-6 text-center">
        <h1 className="text-2xl font-bold mb-2">This post is no longer available</h1>
        <Link to="/" className="text-primary underline">Go to Anant Web Portal</Link>
      </div>
    );
  }
  if (!post) return <div className="min-h-screen pt-28 text-center">Loading...</div>;

  const img = (post.images || []).map((i) => (typeof i === 'string' ? i : i?.url)).find(Boolean);
  const price = isSale ? post.price_expectation_at : post.target_budget_at;
  const who = isSale ? post.seller_username : post.buyer_username;
  const origin = window.location.origin;
  const shareUrl = `${origin}/api/atc/${isSale ? 'sale-post' : 'demand'}/${id}/share${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;
  const open = (post.status || 'OPEN') === 'OPEN';

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="public-atc-post">
      <div className="max-w-xl mx-auto glass-card p-5 rounded-2xl">
        <div className="text-xs font-black text-emerald-300 uppercase mb-2">
          {isSale ? '🏷️ Anant Sellers Bazaar · For sale' : '📢 Anant Trade Center · Demand'} · {isB2B ? 'B2B Wholesale' : 'B2C Retail'}
        </div>
        {img && <img src={img} alt={post.title} className="w-full max-h-80 object-cover rounded-xl mb-3" />}
        <h1 className="text-2xl font-bold mb-2" data-testid="public-atc-title">{post.title}</h1>
        {post.description && <p className="text-sm text-muted-foreground mb-3 whitespace-pre-line">{post.description}</p>}
        <div className="grid grid-cols-2 gap-2 text-sm mb-4">
          <div className="p-2 rounded-lg bg-white/5"><div className="text-[10px] opacity-60">{isSale ? 'Price' : 'Budget'}</div><b>{Number(price || 0).toFixed(0)} AT</b></div>
          <div className="p-2 rounded-lg bg-white/5"><div className="text-[10px] opacity-60">Quantity</div><b>{post.quantity || 1}</b></div>
          {post.city && <div className="p-2 rounded-lg bg-white/5"><div className="text-[10px] opacity-60">City</div><b>{post.city}</b></div>}
          {who && <div className="p-2 rounded-lg bg-white/5"><div className="text-[10px] opacity-60">{isSale ? 'Seller' : 'Posted by'}</div><b>@{who}</b></div>}
        </div>
        {open ? (
          <button onClick={openInApp} data-testid="public-atc-cta"
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-900 font-black">
            {user ? (isSale ? 'Open & make an offer' : 'Open & place a bid') : (isSale ? 'Login to make an offer' : 'Login to place a bid')}
          </button>
        ) : (
          <div className="w-full py-3 rounded-xl bg-white/5 text-center text-sm">This post is closed</div>
        )}
        {!user && (
          <p className="text-xs text-center mt-2 text-muted-foreground">New here? <Link to="/register" className="text-primary underline">Create a free account</Link></p>
        )}
        <div className="mt-4">
          <ShareBar shareUrl={shareUrl} title={post.title}
                    text={`${isSale ? '🏷️ For sale' : '📢 Demand'}: "${post.title}" · ${Number(price || 0).toFixed(0)} AT`}
                    imageUrl={`${origin}/api/atc/${isSale ? 'sale-post' : 'demand'}/${id}/og.png`} testPrefix="public-atc-share" />
        </div>
      </div>
    </div>
  );
}
